import { FC } from 'react'
import { useRecordContext, useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { WorkflowOut } from '../../types/apiRequests'
import { themeColor, xxllncColor } from '../../layout/theme/color'

const MONO_FONT = '"SF Mono", "Fira Code", ui-monospace, monospace'
const ACCENT = xxllncColor.dark100

const VariableChip: FC<{ name: string }> = ({ name }) => (
  <Box
    sx={{
      'display': 'inline-flex',
      'alignItems': 'center',
      'gap': 0.4,
      'px': 1.25,
      'py': 0.5,
      'borderRadius': '6px',
      'background': 'rgba(49,103,155,0.06)',
      'border': '1px solid rgba(49,103,155,0.18)',
      'fontFamily': MONO_FONT,
      'fontSize': 12.5,
      'color': ACCENT,
      'transition': 'border-color 120ms ease, background 120ms ease',
      '&:hover': {
        background: 'rgba(49,103,155,0.10)',
        borderColor: 'rgba(49,103,155,0.30)',
      },
    }}
  >
    <Box component="span" sx={{ opacity: 0.45, fontWeight: 500 }}>{'{{'}</Box>
    <Box component="span" sx={{ fontWeight: 600 }}>{name}</Box>
    <Box component="span" sx={{ opacity: 0.45, fontWeight: 500 }}>{'}}'}</Box>
  </Box>
)

const WorkflowVariablesSection: FC = () => {
  const record = useRecordContext<WorkflowOut>()
  const translate = useTranslate()
  if (!record) return null

  const variables = (record.variables ?? []) as Array<{ name: string }>
  const hasVariables = variables.length > 0

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.06)',
        p: 3,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 0.75 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: themeColor.slate,
            letterSpacing: '-0.01em',
          }}
        >
          {translate('workflow.variables.title')}
        </Typography>
        {hasVariables && (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: themeColor.slate60,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {variables.length}
          </Typography>
        )}
      </Box>

      <Typography
        sx={{ fontSize: 12, color: themeColor.slate60, mb: 2, lineHeight: 1.5 }}
      >
        {translate('workflow.variables.subtitle')}
      </Typography>

      {hasVariables ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {variables.map((v) => <VariableChip key={v.name} name={v.name} />)}
        </Box>
      ) : (
        <Box
          sx={{
            background: 'rgba(0,0,0,0.02)',
            borderRadius: '10px',
            border: '1px dashed rgba(0,0,0,0.10)',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 12, color: themeColor.slate, lineHeight: 1.55 }}>
            {translate('workflow.variables.emptyHelp')}
          </Typography>
          <Box
            sx={{
              fontFamily: MONO_FONT,
              fontSize: 12,
              color: ACCENT,
              background: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: '6px',
              px: 1.5,
              py: 0.75,
              alignSelf: 'flex-start',
            }}
          >
            {'{{ variables.your_variable }}'}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default WorkflowVariablesSection
